<!--
  TimestampInput.vue

  Form input for timestamp fields. Uses `<input type="datetime-local">`;
  `displayed` returns the input-local string, `parseValue` returns the
  storage shape (ISO string or null).
-->
<template>
  <input
    class="input"
    :class="`is-${size}`"
    type="datetime-local"
    :value="formattedValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :readonly="readonly"
    :autofocus="autofocus"
    @input="handleInput"
  />
</template>

<script setup lang="ts">
import { computed } from "vue"
import type { Field } from "@auger/core/fields"
import { useField } from "../useField"

const props = withDefaults(
  defineProps<{
    modelValue?: string | null
    field: Field
    placeholder?: string
    disabled?: boolean
    readonly?: boolean
    autofocus?: boolean
    size?: "small" | "normal" | "medium" | "large"
  }>(),
  {
    modelValue: null,
    placeholder: "",
    disabled: false,
    readonly: false,
    autofocus: false,
    size: "normal",
  }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
}>()

const { handleInput, displayed } = useField(props.field, emit)

// `displayed` returns full ISO; `<input type="datetime-local">` needs
// `YYYY-MM-DDTHH:mm`. Truncate at the minute.
const formattedValue = computed(() => {
  const iso = displayed(props.modelValue) as string
  return iso ? iso.slice(0, 16) : ""
})
</script>
